/**
 * When the sources panel is read-only, and why.
 *
 * Extracted from `SourcesPanel.tsx` (782 lines), where ~130 lines of derived
 * state were computed inline in the component body. This block was the safest
 * piece to move first because it calls **no hooks and has no effects** — it is a
 * pure function of six inputs. That makes it extractable with zero risk of
 * changing render or effect timing (the failure mode that makes decomposing
 * these components hazardous; see the
 * `// Removed onProgressUpdate to prevent infinite loop` comment in
 * LessonViewer for what that looks like when it goes wrong).
 *
 * A plain function rather than a hook, deliberately: nothing here needs React,
 * so a hook would add hook-order constraints for no benefit — and a function is
 * directly unit-testable, which matters because `SourcesPanel` itself has no
 * tests at all.
 *
 * The four rules encoded here were previously only discoverable by reading the
 * component's comments. They are genuine product rules, not plumbing:
 *
 * 1. Being "done" is not enough — `byte_selection` must also have settled.
 * 2. Old versions are read-only.
 * 3. The ingest view only appears for lessons the user added sources to.
 * 4. Total failure suppresses per-row retries.
 */
import type { SourcesView, StepView } from '@/types/pipeline';

export interface LockStateInput {
  /** True in `api` data mode. In mock/empty mode nothing is pipeline-locked. */
  isApi: boolean;
  /** Per-step view map from the page-level `usePipelineStatus` poll. */
  steps?: Record<string, StepView>;
  /** True once the lesson's pipeline is terminal. Only flows in API mode. */
  lessonDone?: boolean;
  /** False when the user is looking at an older version. */
  isViewingLatest: boolean;
  /** Did the user actually attach sources, or is this a synthetic lesson? */
  hasUserSources?: boolean;
  /** Source-ingestion rollup. */
  sourcesView?: SourcesView;
}

export interface LockState {
  /** `byte_selection` has reached a terminal state (or never ran). */
  byteSettled: boolean;
  /** The pipeline is still working, so edits would race it. */
  pipelineLocked: boolean;
  /** Combined lock: old version OR mid-generation. */
  locked: boolean;
  /** Show the "reading your sources" progress view instead of the tabs. */
  sourcesIngesting: boolean;
  /** Every source failed — suppress per-row retries. */
  sourcesAllFailed: boolean;
}

export function deriveLockState({
  isApi,
  steps,
  lessonDone,
  isViewingLatest,
  hasUserSources,
  sourcesView,
}: LockStateInput): LockState {
  // The overall status can read `completed` while byte_selection is re-running
  // — e.g. after a scoped source finishes ingesting and re-fires the context
  // build, or on a re-scope retry. Editing sources / creating a version then
  // would race that rebuild, so stay locked until byte_selection settles
  // (completed/failed, or absent for an off-flow lesson) — not just until the
  // overall status is terminal.
  const byteState = steps?.byte_selection?.state;
  const byteSettled =
    byteState === undefined ||
    byteState === 'completed' ||
    byteState === 'failed';

  const pipelineLocked = isApi && (lessonDone !== true || !byteSettled);

  // Used by the prompt editor, the staged action bar, and the source rows.
  const locked = !isViewingLatest || pipelineLocked;

  // The user's own sources are still ingesting — hide the tabs and show a
  // single "reading your sources" progress view until ingestion is terminal.
  // ONLY for lessons where the user actually added sources: synthetic /
  // no-source lessons have nothing to watch here, so they keep the normal tabs
  // (the middle panel narrates "Gathering study material" for them instead).
  const sourcesIngesting =
    isApi &&
    hasUserSources === true &&
    (sourcesView?.overall === 'processing' ||
      sourcesView?.overall === 'queued');

  // Total-failure state: every source failed. Suppress the per-row retries —
  // the centre panel's single "Retry all sources" is the right action, because
  // a per-row retry would rebuild the lesson on just one source. Partial
  // failures keep their per-row retry.
  const sourcesAllFailed = isApi && sourcesView?.overall === 'failed';

  return {
    byteSettled,
    pipelineLocked,
    locked,
    sourcesIngesting,
    sourcesAllFailed,
  };
}
